import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  Collapse,
  TablePagination,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';
import Layout from './Layout';

const RelatorioResultados = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  
  // Estados de paginação
  const [page, setPage] = useState(0);
  // Paginação padrão: 1000 por página
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [totalCount, setTotalCount] = useState(0);
  // Ordem de colunas fornecida pelo backend (procedure)
  const respColumnsRef = React.useRef(null);
  
  // Estados de filtros (default = mês vigente)
  const firstDayCurrentMonth = React.useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }, []);
  const [filtros, setFiltros] = useState({
    corretora: '',
    comercial: '',
    tipo_atendimento: '',
    categoria: '',
    periodo_inicial: firstDayCurrentMonth,
    periodo_final: firstDayCurrentMonth
  });

  // Função para buscar dados
  const fetchDados = async (currentPage = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage + 1,
        per_page: rowsPerPage,
        ...Object.fromEntries(
          Object.entries(filtros).filter(([key, value]) => {
            if (value === null || value === '') return false;
            if (key.includes('periodo') && value instanceof Date) {
              return true;
            }
            return value;
          }).map(([key, value]) => {
            if (key.includes('periodo') && value instanceof Date) {
              // Converter data para formato YYYY-MM-DD (primeiro dia do mês)
              const year = value.getFullYear();
              const month = String(value.getMonth() + 1).padStart(2, '0');
              return [key, `${year}-${month}-01`];
            }
            return [key, value];
          })
        )
      });

      const response = await api.get(`/relatorio-resultados?${params}`);
      respColumnsRef.current = response.data?.columns || null;
      setDados(response.data.resultados || []);
      setTotalCount(response.data.pagination?.total || 0);
      
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados iniciais

  useEffect(() => {
    fetchDados(page);
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funções de controle
  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const aplicarFiltros = () => {
    // Exigir período completo quando qualquer um dos campos for preenchido
    const hasIni = filtros.periodo_inicial instanceof Date;
    const hasFim = filtros.periodo_final instanceof Date;
    if ((hasIni && !hasFim) || (!hasIni && hasFim)) {
      setError('Informe Período Inicial e Período Final.');
      return;
    }
    setError(null);
    setPage(0);
    fetchDados(0);
  };

  const limparFiltros = () => {
    setFiltros({
      corretora: '',
      comercial: '',
      tipo_atendimento: '',
      categoria: '',
      periodo_inicial: null,
      periodo_final: null
    });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Exportar registros em tela para CSV (Excel abre normalmente)
  const exportarExcel = async () => {
    try {
      // Buscar todos os dados com os filtros atuais (ignorando paginação)
      setLoading(true);
      const paramsFull = new URLSearchParams({
        page: 1,
        per_page: 100000, // grande o suficiente para trazer todos
        ...Object.fromEntries(
          Object.entries(filtros).filter(([key, value]) => {
            if (value === null || value === '') return false;
            if (key.includes('periodo') && value instanceof Date) return true;
            return value;
          }).map(([key, value]) => {
            if (key.includes('periodo') && value instanceof Date) {
              const year = value.getFullYear();
              const month = String(value.getMonth() + 1).padStart(2, '0');
              return [key, `${year}-${month}-01`];
            }
            return [key, value];
          })
        )
      });

      const resp = await api.get(`/relatorio-resultados?${paramsFull}`);
      const dadosParaExportar = resp.data?.resultados || [];
      const exportOrderCols = resp.data?.columns || (dadosParaExportar[0] ? Object.keys(dadosParaExportar[0]) : []);
      if (!Array.isArray(dadosParaExportar) || dadosParaExportar.length === 0) {
        setError('Não há dados para exportar com os filtros atuais.');
        setLoading(false);
        return;
      }

      // Exportar como XLSX - conversão robusta de valores
      const XLSX = await import('xlsx');

      const parseToNumber = (val) => {
        // Null/undefined
        if (val === null || val === undefined || val === '') return null;
        
        // Já é número
        if (typeof val === 'number') {
          return isNaN(val) || !isFinite(val) ? null : val;
        }
        
        // String vazia
        const s = String(val).trim();
        if (!s) return null;
        
        // Detectar se é formato brasileiro (1.234,56) ou americano (1,234.56)
        // Se contém vírgula e ponto, assumir formato brasileiro
        if (s.includes(',') && s.includes('.')) {
          // Formato brasileiro: 1.234.567,89
          const normalized = s.replace(/\./g, '').replace(',', '.');
          const num = parseFloat(normalized);
          return isNaN(num) || !isFinite(num) ? null : num;
        }
        // Se contém apenas vírgula, assumir decimal brasileiro
        else if (s.includes(',') && !s.includes('.')) {
          // Formato: 1234,56
          const normalized = s.replace(',', '.');
          const num = parseFloat(normalized);
          return isNaN(num) || !isFinite(num) ? null : num;
        }
        // Se contém apenas ponto, pode ser milhar americano ou decimal
        else if (s.includes('.')) {
          // Se há mais de um ponto OU ponto não está nas últimas 3 posições = formato milhar americano
          const pontos = (s.match(/\./g) || []).length;
          const ultimoPonto = s.lastIndexOf('.');
          const posicoesAposUltimoPonto = s.length - ultimoPonto - 1;
          
          if (pontos > 1 || posicoesAposUltimoPonto !== 2) {
            // Formato americano: 1,234.567 ou 1.234.567
            const normalized = s.replace(/\./g, '');
            const num = parseFloat(normalized);
            return isNaN(num) || !isFinite(num) ? null : num;
          } else {
            // Decimal com ponto: 1234.56
            const num = parseFloat(s);
            return isNaN(num) || !isFinite(num) ? null : num;
          }
        }
        // Apenas dígitos
        else {
          const num = parseFloat(s);
          return isNaN(num) || !isFinite(num) ? null : num;
        }
      };

      // Log dos primeiros dados para debug
      if (dadosParaExportar.length > 0) {
        console.log('Debug Excel Export - Primeira linha:', dadosParaExportar[0]);
        const firstRow = dadosParaExportar[0];
        exportOrderCols.forEach(key => {
          if (isCurrencyCol(key)) {
            const originalValue = firstRow[key];
            const parsedValue = parseToNumber(originalValue);
            console.log(`${key}: "${originalValue}" (${typeof originalValue}) -> ${parsedValue}`);
          }
        });
      }

      const rowsOut = dadosParaExportar.map((row) => {
        const out = {};
        exportOrderCols.forEach((key) => {
          const v = row[key];
          if (isCurrencyCol(key) || isCountCol(key)) {
            const num = parseToNumber(v);
            out[key] = num !== null ? num : (v ?? '');
          } else {
            out[key] = v ?? '';
          }
        });
        return out;
      });

      const ws = XLSX.utils.json_to_sheet(rowsOut, { header: exportOrderCols });
      
      // Aplicar formato às células numéricas
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const colName = exportOrderCols[C];
        const isNumCol = isCurrencyCol(colName) || isCountCol(colName);
        if (!isNumCol) continue;
        
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && typeof cell.v === 'number') {
            cell.t = 'n';
            cell.z = isCurrencyCol(colName) ? '"R$ "#,##0.00' : '#,##0';
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Relatório');
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
      XLSX.writeFile(wb, `relatorio_resultados_${ts}.xlsx`);
      setLoading(false);
    } catch (e) {
      console.error('Erro ao exportar CSV:', e);
      setError('Erro ao exportar. Tente novamente.');
      setLoading(false);
    }
  };

  // Helpers de formatação para renderização dinâmica
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    const num = typeof value === 'number' ? value : Number(value);
    if (!isNaN(num) && isFinite(num)) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
    }
    return String(value || '-');
  };

  const isCountCol = (name) => /qtd|count/i.test(name);
  const isCurrencyCol = (name) => !isCountCol(name) && /hdi|suhai|tokio|ituran|darwin|justos|yelum|total|venda|cancel/i.test(name);

  // Colunas fixas à esquerda e suas larguras (px)
  const stickyCols = React.useMemo(() => ([
    { key: 'CORRETORA', width: 240 },
    { key: 'COMERCIAL', width: 180 },
    { key: 'TIPO ATENDIMENTO', width: 180 },
  ]), []);

  const stickyKeys = React.useMemo(() => stickyCols.map(c => c.key), [stickyCols]);
  const stickyLeftMap = React.useMemo(() => {
    const map = {};
    let acc = 0;
    for (const c of stickyCols) {
      map[c.key] = acc;
      acc += c.width;
    }
    return map;
  }, [stickyCols]);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Relatório de Resultados
            </Typography>
            {(filtros.periodo_inicial || filtros.periodo_final) && (
              <Typography variant="subtitle1" color="textSecondary">
                Competência: {filtros.periodo_inicial ? filtros.periodo_inicial.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }) : ''} 
                {filtros.periodo_inicial && filtros.periodo_final ? ' a ' : ''}
                {filtros.periodo_final ? filtros.periodo_final.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }) : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            >
              Filtros
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportarExcel}
              disabled={loading || !dados?.length}
            >
              Exportar Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchDados(page)}
              disabled={loading}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        {/* Mensagem de erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Mensagem informativa sobre filtros de período */}
        {(filtros.periodo_inicial || filtros.periodo_final) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Filtro de Período Aplicado:</strong> Exibindo dados para a competência selecionada
            {filtros.periodo_inicial && ` de ${filtros.periodo_inicial.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })}`}
            {filtros.periodo_final && ` até ${filtros.periodo_final.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })}`}
          </Alert>
        )}

        {/* Painel de Filtros */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filtros de Pesquisa
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Corretora"
                    value={filtros.corretora}
                    onChange={(e) => handleFilterChange('corretora', e.target.value)}
                    placeholder="Nome da corretora"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Comercial"
                    value={filtros.comercial}
                    onChange={(e) => handleFilterChange('comercial', e.target.value)}
                    placeholder="Nome do comercial"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Tipo de Atendimento"
                    value={filtros.tipo_atendimento}
                    onChange={(e) => handleFilterChange('tipo_atendimento', e.target.value)}
                    placeholder="Tipo de atendimento"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Categoria"
                    value={filtros.categoria}
                    onChange={(e) => handleFilterChange('categoria', e.target.value)}
                    placeholder="Categoria"
                  />
                </Grid>


                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Período Inicial (Mês/Ano)"
                      value={filtros.periodo_inicial}
                      onChange={(newValue) => handleFilterChange('periodo_inicial', newValue)}
                      views={['year', 'month']}
                      format="MM/yyyy"
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          placeholder: "MM/AAAA"
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Período Final (Mês/Ano)"
                      value={filtros.periodo_final}
                      onChange={(newValue) => handleFilterChange('periodo_final', newValue)}
                      views={['year', 'month']}
                      format="MM/yyyy"
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          placeholder: "MM/AAAA"
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={aplicarFiltros}
                    sx={{ height: '56px' }}
                    disabled={loading}
                  >
                    Aplicar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={limparFiltros}
                    sx={{ height: '56px' }}
                  >
                    Limpar Filtros
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Tabela de Resultados */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Resultados ({totalCount} registros)
              </Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto', boxShadow: 'none' }}>
                  <Table stickyHeader sx={{ minWidth: 'max-content' }}>
                    <TableHead>
                      <TableRow>
                        {dados && dados.length > 0 && (respColumnsRef.current || Object.keys(dados[0])).map((col) => {
                          const isSticky = stickyKeys.includes(col);
                          const left = stickyLeftMap[col] ?? 0;
                          const width = (stickyCols.find(c => c.key === col)?.width) || undefined;
                          return (
                            <TableCell
                              key={col}
                              sx={{
                                fontWeight: 'bold',
                                position: isSticky ? 'sticky' : 'static',
                                left: isSticky ? left : undefined,
                                zIndex: isSticky ? 20 : 1,
                                backgroundColor: isSticky ? 'background.paper' : undefined,
                                minWidth: width,
                                width: width,
                              }}
                            >
                              {col}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.map((row, index) => (
                        <TableRow key={index} hover>
                          {(respColumnsRef.current || Object.keys(dados[0] || {})).map((col) => {
                            const isSticky = stickyKeys.includes(col);
                            const left = stickyLeftMap[col] ?? 0;
                            const width = (stickyCols.find(c => c.key === col)?.width) || undefined;
                            return (
                              <TableCell
                                key={col}
                                align={isCurrencyCol(col) || isCountCol(col) ? 'right' : 'left'}
                                sx={{
                                  position: isSticky ? 'sticky' : 'static',
                                  left: isSticky ? left : undefined,
                                  zIndex: isSticky ? 15 : 0,
                                  backgroundColor: isSticky ? 'background.paper' : undefined,
                                  minWidth: width,
                                  width: width,
                                }}
                              >
                                {isCurrencyCol(col) ? formatNumber(row[col]) : (row[col] ?? '-')}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                      {dados.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={23} align="center" sx={{ py: 3 }}>
                            <Typography color="textSecondary">
                              Nenhum resultado encontrado
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[50, 100, 500, 1000]}
                  labelRowsPerPage="Registros por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default RelatorioResultados; 